import { resolveCfNode } from './slot-schema';
import type { MovePosition } from './types';

/** Parse html into a detached host div (SSR-safe guard for jsdom/agent paths). */
function toHost(html: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const tmpl = document.createElement('template');
  tmpl.innerHTML = html;
  const host = document.createElement('div');
  host.appendChild(tmpl.content);
  return host;
}

/** Set the text content of the cf-id-addressed node. No-op if not found. */
export function editNodeText(html: string, cfNodeId: string, text: string): string {
  const host = toHost(html);
  if (!host) return html;
  const target = resolveCfNode(host, cfNodeId);
  if (!target) return html;
  target.textContent = text;
  return host.innerHTML;
}

/** Remove the cf-id-addressed node from the html. No-op if not found. */
export function deleteNode(html: string, cfNodeId: string): string {
  const host = toHost(html);
  if (!host) return html;
  const target = resolveCfNode(host, cfNodeId);
  if (!target) return html;
  target.remove();
  return host.innerHTML;
}

/** Clone the cf-id-addressed node and insert the copy immediately after it.
 *  cf-ids are stripped from the clone (the copy + its descendants) so a
 *  follow-up injectCfNodeIds stamps fresh, collision-free ids. No-op if not
 *  found. */
export function duplicateNode(html: string, cfNodeId: string): string {
  const host = toHost(html);
  if (!host) return html;
  const target = resolveCfNode(host, cfNodeId);
  if (!target) return html;
  const clone = target.cloneNode(true) as Element;
  clone.removeAttribute('data-cf-id');
  clone.querySelectorAll('[data-cf-id]').forEach((el) => el.removeAttribute('data-cf-id'));
  target.after(clone);
  return host.innerHTML;
}

export type MediaKind = 'img' | 'video' | 'background' | 'lottie' | 'none';

/** What media slot, if any, a node exposes. */
export function nodeMediaKind(el: Element): MediaKind {
  if (el.hasAttribute('data-lottie')) return 'lottie';
  const tag = el.tagName.toLowerCase();
  if (tag === 'img') return 'img';
  if (tag === 'video' || tag === 'source') return 'video';
  if (tag === 'picture') return 'img';
  const style = (el as HTMLElement).getAttribute('style') ?? '';
  if (/background(-image)?\s*:/.test(style) && /url\(/.test(style)) return 'background';
  return 'none';
}

/** Replace the inner HTML of the cf-id-addressed node. Parses real markup
 *  (innerHTML, NOT textContent) so tags render as elements, not escaped text.
 *  No-op if the node is not found. */
export function setNodeInnerHtml(html: string, cfNodeId: string, fragmentHtml: string): string {
  const host = toHost(html);
  if (!host) return html;
  const target = resolveCfNode(host, cfNodeId);
  if (!target) return html;
  target.innerHTML = fragmentHtml;
  return host.innerHTML;
}

/** Insert real HTML relative to refCfNodeId. pos 'inside' appends as the last
 *  child; 'before'/'after' insert as siblings. Order-preserving via a
 *  DocumentFragment. No-op if ref is not found. */
export function insertNodeHtml(
  html: string,
  refCfNodeId: string,
  fragmentHtml: string,
  pos: MovePosition,
): string {
  const host = toHost(html);
  if (!host) return html;
  const ref = resolveCfNode(host, refCfNodeId);
  if (!ref) return html;
  const parsed = toHost(fragmentHtml);
  if (!parsed) return html;
  const frag = document.createDocumentFragment();
  while (parsed.firstChild) frag.appendChild(parsed.firstChild);
  if (pos === 'before') ref.before(frag);
  else if (pos === 'after') ref.after(frag);
  else ref.appendChild(frag); // 'inside' = append
  return host.innerHTML;
}

/** Point the cf-id-addressed media node at `ref` (asset:// or plain URL). */
export function setNodeMediaRef(html: string, cfNodeId: string, ref: string): string {
  const host = toHost(html);
  if (!host) return html;
  const el = resolveCfNode(host, cfNodeId) as HTMLElement | null;
  if (!el) return html;
  const kind = nodeMediaKind(el);
  if (kind === 'lottie') {
    el.setAttribute('data-lottie', ref);
  } else if (kind === 'img' || kind === 'video') {
    el.setAttribute('src', ref);
  } else if (kind === 'background') {
    const style = el.getAttribute('style') ?? '';
    const next = style.replace(/background(-image)?\s*:\s*[^;]*/i, `background-image:url("${ref}")`);
    el.setAttribute('style', next.includes('url(') ? next : `${style};background-image:url("${ref}")`);
  } else {
    // No media slot — set src anyway (caller gates on nodeMediaKind, so rare).
    el.setAttribute('src', ref);
  }
  return host.innerHTML;
}

/** Rewrite the cf-id-addressed lottie element's slot-override attribute. */
export function setLottieSlotsAttr(
  html: string,
  cfNodeId: string,
  slots: Record<string, number | number[] | string>,
): string {
  const host = toHost(html);
  if (!host) return html;
  const el = resolveCfNode(host, cfNodeId) as HTMLElement | null;
  if (!el || !el.hasAttribute('data-lottie')) return html;
  el.setAttribute('data-lottie-slots', JSON.stringify(slots));
  return host.innerHTML;
}

/**
 * Move the cf-id-addressed node to a position relative to refCfNodeId.
 * Returns html unchanged (no-op) when either id is missing, ref === node, or
 * ref is the node itself or a descendant of it (cycle). The node keeps its
 * data-cf-id, so downstream references survive the move.
 */
export function moveNode(
  html: string,
  cfNodeId: string,
  refCfNodeId: string,
  pos: MovePosition,
): string {
  const host = toHost(html);
  if (!host) return html;
  const node = resolveCfNode(host, cfNodeId);
  const ref = resolveCfNode(host, refCfNodeId);
  if (!node || !ref || node === ref || node.contains(ref)) return html;
  if (pos === 'before') ref.before(node);
  else if (pos === 'after') ref.after(node);
  else if (pos === 'inside') ref.appendChild(node); // append to end of the container
  else { const _exhaustive: never = pos; void _exhaustive; }
  return host.innerHTML;
}
